/* eslint-disable @typescript-eslint/naming-convention */
import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import type {
    BacklinksBacklinksCreateParams,
    BacklinksBacklinksCreatePayload,
    BacklinksBacklinksExcelCreateParams,
    BacklinksRefdomainsCreateParams,
    BacklinksRefdomainsExcelCreateParams,
    BacklinksTimeseriesExcelListParams,
    BacklinksTimeseriesNewlostExcelListParams,
} from "@similarweb/pro-api-client";
import type { AnyObject } from "@similarweb/pro-logic-components";
import { serverResponseFormat } from "@similarweb/pro-logic-components";
import type { ColumnsType, ITableProps } from "@similarweb/pro-logic-components/dist/table/utils/types";

import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { abbrNumberFilter } from "app/filters/ngFilters";
import {
    GRANULARITY_INDEX,
    TIME_RANGES,
    TIME_RANGES_INDEX,
    BacklinksApiEP,
    DEFAULT_PAGE_SIZE,
    MAX_LIMIT_RECORDS,
    chartTimeGranularityOptions,
    TODAY,
    API_DATE_REQUEST_FORMAT,
    START_DATE_13_M,
} from "app/pages/digital-marketing/backlinks/shared/BacklinksConsts";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { DefaultFetchService } from "app/services/fetchService";

import type { IDynamicFilterRuleOverride } from "../reports/backlinks/components/filters/include-exclude-filter/include-exclude.types";
import {
    dynamicRulesFromQueryString,
    dynamicRulesToApiQueryPayloadFromQueryString,
} from "../reports/backlinks/components/filters/include-exclude-filter/include-exclude.utils";

import type { BacklinkExpectedURLParams, PaginatedResponse } from "./BacklinksTypes";

const fetchService = DefaultFetchService.getInstance();

export const numFilter: (
    input: string | number,
    removeTrailingZeros?: boolean,
    digitsOverride?: number,
) => string | number = abbrNumberFilter();

/**
 * Configures the pagination settings for a table.
 *
 * @template T - The type of the data objects in the paginated response.
 * @param {Partial<PaginatedResponse<T>> & { onChange?: Required<ITableProps<T>>["pagination"]["onChange"] }} paginationData
 * - The pagination data which includes optional properties for page size, total records, current page, and an onChange event handler.
 * @returns {ITableProps<T>["pagination"]}
 * - The pagination configuration object for the table.
 *
 * Given partial pagination data and an optional onChange callback, returns a fully-formed pagination config for use in a table component.
 * If the total number of records is greater than MAX_LIMIT_RECORDS, the total will be set to MAX_LIMIT_RECORDS, as the api has a restriction of 10,000 records in paginated data.
 */
export function tablePaginationConfig<T extends AnyObject>(
    paginationData: Partial<PaginatedResponse<T>> & { onChange?: Required<ITableProps<T>>["pagination"]["onChange"] },
): ITableProps<T>["pagination"] {
    const actualPageSize = paginationData?.Records?.length ?? 0;
    let expectedPageSize = paginationData?.PageSize ?? DEFAULT_PAGE_SIZE;
    // This pageSize mismatch is a critical error that should be reported to Sentry because it can break the table functionality
    if (typeof actualPageSize === "number" && actualPageSize > 0 && actualPageSize !== expectedPageSize) {
        // This might happen when the API return the default page size of 100, but the actual data is less than that
        // In this case, we set the expectedPageSize to the actualPageSize to avoid breaking the table logic
        expectedPageSize = actualPageSize;
    }
    return {
        pageSize: expectedPageSize,
        total: Math.min(paginationData?.TotalRecords ?? 0, MAX_LIMIT_RECORDS),
        current: paginationData?.Page ?? 1,
        onChange: paginationData?.onChange,
    };
}

const parseOptionalIntParam = (value?: string): number | undefined => (value ? parseInt(value, 10) : undefined);

const parseOptionalBooleanParam = (value?: string): boolean | undefined =>
    typeof value === "string" ? value === "true" : undefined;

export const buildBacklinksBacklinksCreateParams = (
    params: BacklinkExpectedURLParams & { pageFilterParsed?: IChosenItemParams[number] },
    pagination: { page: number; pageSize: number },
): BacklinksBacklinksCreateParams =>
    ({
        page: pagination.page,
        pageSize: pagination.pageSize,
        status: params.status ?? "All",
        followType: params.follow ?? "All",
        key: params.key ?? params.pageFilterParsed?.url,
        asc: params.asc === "true",
        sort: params.sort,
        domainScoreFrom: parseOptionalIntParam(params.dtsFrom),
        domainScoreTo: parseOptionalIntParam(params.dtsTo),
        isBroken: parseOptionalBooleanParam(params.brokenOnly),
    }) satisfies BacklinksBacklinksCreateParams;

export const buildBacklinksRefdomainsCreateParams = (
    params: BacklinkExpectedURLParams & { pageFilterParsed?: IChosenItemParams[number] },
    pagination: { page: number; pageSize: number },
): BacklinksRefdomainsCreateParams =>
    ({
        page: pagination.page,
        pageSize: pagination.pageSize,
        status: params.status ?? "All",
        key: params.key ?? params.pageFilterParsed?.url,
        asc: params.asc === "true",
        sort: params.sort,
        rankFrom: parseOptionalIntParam(params.dtsFrom),
        rankTo: parseOptionalIntParam(params.dtsTo),
    }) satisfies BacklinksRefdomainsCreateParams;

/**
 * Generates the URL for downloading an Excel file based on the specified type and parameters.
 *
 * @param type - The type of Excel download, currently supports "domainsExcel".
 * @param params - The parameters required to generate the URL.
 * @param params.asc - A string indicating whether the sorting is ascending ("true") or descending ("false").
 * @param params.dtsFrom - The minimum domain score filter.
 * @param params.dtsTo - The maximum domain score filter.
 * @param params.key - The key for filtering.
 * @param params.pageFilter - A JSON string representing the page filter.
 * @param params.sort - The field by which to sort the results.
 * @param params.status - The status filter.
 *
 * @returns The URL string for downloading the Excel file.
 */
export const getExcelDownloadURL = (
    type: keyof Pick<
        typeof BacklinksApiEP,
        "domainsExcel" | "timeseriesExcel" | "newLostTimeseriesExcel" | "backlinksExcel"
    >,
    params: BacklinkExpectedURLParams = {},
) => {
    const pageFilter = JSON.parse(params.pageFilter ?? "[]") as IChosenItemParams;
    const firstFilterItem = pageFilter[0];

    switch (type) {
        case "domainsExcel": {
            const requestData = {
                key: params.key ?? firstFilterItem?.url,
                asc: params.asc === "true",
                sort: params.sort,
                rankFrom: parseOptionalIntParam(params.dtsFrom),
                rankTo: parseOptionalIntParam(params.dtsTo),
                status: params.status,
            } satisfies BacklinksRefdomainsExcelCreateParams;
            return `${BacklinksApiEP[type]}?${fetchService.requestParams(requestData)}`;
        }
        case "timeseriesExcel": {
            const requestData = {
                key: params.key ?? firstFilterItem?.url,
                from: START_DATE_13_M.format(API_DATE_REQUEST_FORMAT),
                to: TODAY.format(API_DATE_REQUEST_FORMAT),
                isDaily: true,
                isWindow: false,
            } satisfies BacklinksTimeseriesExcelListParams;
            return `${BacklinksApiEP[type]}?${fetchService.requestParams(requestData)}`;
        }
        case "newLostTimeseriesExcel": {
            const requestData = {
                key: params.key ?? firstFilterItem?.url,
                from: START_DATE_13_M.format(API_DATE_REQUEST_FORMAT),
                to: TODAY.format(API_DATE_REQUEST_FORMAT),
                isDaily: true,
                isWindow: false,
            } satisfies BacklinksTimeseriesNewlostExcelListParams;
            return `${BacklinksApiEP[type]}?${fetchService.requestParams(requestData)}`;
        }
        case "backlinksExcel": {
            const requestData = {
                key: params.key ?? firstFilterItem?.url,
                asc: params.asc === "true",
                sort: params.sort,
                status: params.status,
                domainScoreFrom: parseOptionalIntParam(params.dtsFrom),
                domainScoreTo: parseOptionalIntParam(params.dtsTo),
                followType: params.follow,
                isBroken: parseOptionalBooleanParam(params.brokenOnly),
            } satisfies BacklinksBacklinksExcelCreateParams;
            return `${BacklinksApiEP[type]}?${fetchService.requestParams(requestData)}`;
        }
        default:
            return "";
    }
};

/**
 * Custom hook that extracts and parses expected backlink parameters from the current state and parameters.
 *
 * @returns {BacklinkExpectedURLParams & { pageFilterParsed?: IChosenItemParams[number] }}
 * An object containing the expected backlink parameters along with an optional parsed page filter item.
 *
 * The returned object includes:
 * - All properties from `BacklingExpectedURLParams`.
 * - `pageFilterParsed`: The first item from the parsed `pageFilter` array, if available.
 */
export const useExpectedBacklinksParams = (): BacklinkExpectedURLParams & {
    pageFilterParsed?: IChosenItemParams[number];
    includeExcludeParsed?: IDynamicFilterRuleOverride[];
    includeExcludeApiParsed?: BacklinksBacklinksCreatePayload;
} => {
    const { params }: { params: BacklinkExpectedURLParams } = useCurrentStateAndParams();
    const pageFilter = JSON.parse(params.pageFilter ?? "[]") as IChosenItemParams;
    const firstFilterItem = pageFilter[0];
    const includeExcludeParsed = dynamicRulesFromQueryString(params.includeExclude);
    const includeExcludeApiParsed = dynamicRulesToApiQueryPayloadFromQueryString(params.includeExclude);

    return { ...params, pageFilterParsed: firstFilterItem, includeExcludeParsed, includeExcludeApiParsed };
};

/**
 * Calculates a date limit based on a specified range index.
 *
 * @param rangeIndex - The index representing the time range to calculate the limit for.
 *                     Supported values correspond to predefined constants in `TIME_RANGES_INDEX`.
 *                     - `TIME_RANGES_INDEX.DAYS_28`: Returns the date 28 days ago from today.
 *                     - `TIME_RANGES_INDEX.MONTH_3`: Returns the start of the month 3 months ago.
 *                     - `TIME_RANGES_INDEX.MONTH_6`: Returns the start of the month 6 months ago.
 *                     - `TIME_RANGES_INDEX.MONTH_13`: Returns the start of the month 13 months ago.
 * @returns A `dayjs.Dayjs` object representing the calculated date limit. If the range index
 *          does not match any predefined value, the function returns today's date.
 */
function getRangeLimitDate(rangeIndex: number, granularityIndex?: number): dayjs.Dayjs {
    const today = dayjs().endOf("day"); // Get the end of the current day
    const isWeekly = granularityIndex === GRANULARITY_INDEX.WEEKLY; // Check if the granularity is weekly
    switch (rangeIndex) {
        case TIME_RANGES_INDEX.DAYS_28: {
            if (isWeekly) {
                return today.subtract(TIME_RANGES.DAYS_28, "day").startOf("isoWeek"); // Calculate the start of the week 28 days ago
            }
            return today.subtract(TIME_RANGES.DAYS_28, "day"); // Calculate the date 28 days ago
        }
        case TIME_RANGES_INDEX.MONTH_3: {
            return today.subtract(TIME_RANGES.MONTH_3, "months").startOf("month"); // Calculate the date 3 months ago
        }
        case TIME_RANGES_INDEX.MONTH_6: {
            return today.subtract(TIME_RANGES.MONTH_6, "months").startOf("month"); // Calculate the date 6 months ago
        }
        case TIME_RANGES_INDEX.MONTH_13:
            return today.subtract(TIME_RANGES.MONTH_13, "months").startOf("month"); // Calculate the date 12 months ago
        default:
            return today; // Return today's date if range index does not match
    }
}

/**
 * Aggregates data based on the specified granularity index.
 *
 * @template D - The type of the data objects, which must include a `Date` property of type `Date` or `string`.
 * @template N - The key of the new field in the data objects, which must be of type `number`.
 * @template O - The key of the lost field in the data objects, which must be of type `number`.
 * @param {number} granularityIndex - The index representing the granularity to aggregate by.
 * @param {D[]} data - The array of data objects to aggregate.
 * @param {N} newField - The key of the new field to aggregate.
 * @param {O} lostField - The key of the lost field to aggregate.
 * @returns {D[]} - The aggregated array of data objects.
 *
 * @example
 * const data = [
 *   { Date: '2023-01-01', new: 1, lost: 2 },
 *   { Date: '2023-01-02', new: 3, lost: 4 },
 * ];
 * const aggregatedData = filterDataByGranularity(GRANULARITY_INDEX.WEEKLY, data, 'new', 'lost');
 * // Returns data objects aggregated by week.
 *
 * @remarks
 * The function uses the `dayjs` library to handle date comparisons and aggregations.
 * The `granularityIndex` parameter should correspond to one of the predefined constants in `GRANULARITY_INDEX`.
 * If the `granularityIndex` does not match any case, the original data array is returned unaggregated.
 */
export function filterAggregateDataByGranularity<
    D extends { Date: Date | string },
    N extends keyof D,
    O extends keyof D,
>({
    granularityIndex,
    data,
    newField,
    lostField,
    rangeIndex,
}: {
    granularityIndex: number;
    data: D[];
    newField: D[N] extends number ? N : never;
    lostField: D[O] extends number ? O : never;
    rangeIndex?: number;
}): D[] {
    const maxRangeDateLimit = rangeIndex ? getRangeLimitDate(rangeIndex) : null;
    switch (granularityIndex) {
        case GRANULARITY_INDEX.DAILY: // Daily granularity, no aggregation needed
            return data;
        case GRANULARITY_INDEX.WEEKLY: {
            // Weekly granularity
            const aggregatedData: Record<string, D> = {};
            data.forEach((item) => {
                const itemDate = dayjs.utc(item.Date);
                const startOfTheWeek = itemDate.startOf("isoWeek");
                // Check if the item date is before the max range limit
                // This means the item is not complete and should not be included in the aggregation
                if (maxRangeDateLimit && startOfTheWeek.isBefore(maxRangeDateLimit)) {
                    return;
                }
                const dateKey = startOfTheWeek.format(serverResponseFormat); // Start of the week
                if (!aggregatedData[dateKey]) {
                    aggregatedData[dateKey] = { ...item, Date: startOfTheWeek.valueOf() }; // Initialize the week entry
                } else {
                    // Aggregate new and lost fields
                    (aggregatedData[dateKey][newField] as unknown as number) += item[newField] as unknown as number;
                    (aggregatedData[dateKey][lostField] as unknown as number) += item[lostField] as unknown as number;
                }
            });

            return Object.values(aggregatedData);
        }
        case GRANULARITY_INDEX.MONTHLY: {
            // Monthly granularity
            const aggregatedData: Record<string, D> = {};
            data.forEach((item) => {
                const itemDate = dayjs.utc(item.Date);
                const year = itemDate.year();
                // Month is 0-indexed, so we need to add 1 to get the correct month
                const month = itemDate.month() + 1;
                const dateKey = `${year}-${month}`; // Year and month key
                if (!aggregatedData[dateKey]) {
                    aggregatedData[dateKey] = { ...item, Date: itemDate.valueOf() }; // Initialize the month entry
                } else {
                    // Aggregate new and lost fields
                    (aggregatedData[dateKey][newField] as unknown as number) += item[newField] as unknown as number;
                    (aggregatedData[dateKey][lostField] as unknown as number) += item[lostField] as unknown as number;
                }
            });
            return Object.values(aggregatedData);
        }
        default:
            return data; // Return original data if granularity does not match
    }
}

/**
 * Filters data based on a date range index.
 *
 * @template D - The type of the data objects, which must include a `Date` property of type `Date` or `string`.
 * @param {number} rangeIndex - The index representing the date range to filter by.
 * @param {D[]} data - The array of data objects to filter.
 * @returns {D[]} - The filtered array of data objects.
 *
 * @example
 * const data = [
 *   { Date: '2023-01-01', value: 1 },
 *   { Date: '2023-02-01', value: 2 },
 * ];
 * const filteredData = filterDataFromDate(TIME_RANGES_INDEX.DAYS_28, data);
 * // Returns data objects with dates within the last 28 days.
 *
 * @remarks
 * The function uses the `dayjs` library to handle date comparisons.
 * The `rangeIndex` parameter should correspond to one of the predefined constants in `TIME_RANGES_INDEX`.
 * If the `rangeIndex` does not match any case, the original data array is returned unfiltered.
 */
export function filterDataFromDate<D extends { Date: Date | string }>(
    rangeIndex: number,
    data: D[],
    granularityIndex?: number,
): D[] {
    switch (rangeIndex) {
        case TIME_RANGES_INDEX.DAYS_28: {
            const limitDate = getRangeLimitDate(rangeIndex, granularityIndex); // Calculate the date 28 days ago
            return data.filter((item) => dayjs.utc(item.Date).isSameOrAfter(limitDate)); // Filter data within the last 28 days
        }
        case TIME_RANGES_INDEX.MONTH_3: {
            const limitDate = getRangeLimitDate(rangeIndex); // Calculate the date 3 months ago
            return data.filter((item) => dayjs.utc(item.Date).isSameOrAfter(limitDate)); // Filter data within the last 3 months
        }
        case TIME_RANGES_INDEX.MONTH_6: {
            const limitDate = getRangeLimitDate(rangeIndex); // Calculate the date 6 months ago
            return data.filter((item) => dayjs.utc(item.Date).isSameOrAfter(limitDate)); // Filter data within the last 6 months
        }
        case TIME_RANGES_INDEX.MONTH_13:
            return data; // No filtering for 12 months range, return all data
        default:
            return data; // Return original data if range index does not match
    }
}

/**
 * Determines if the current granularity is allowed within the specified time range.
 *
 * @param {Object} params - The parameters for the function.
 * @param {number} params.granularityIndex - The index representing the granularity (e.g., daily, weekly, monthly).
 * @param {number} params.rangeIndex - The index representing the time range (e.g., 28 days, 3 months, 6 months, 12 months).
 * @returns {boolean} - Returns `true` if the granularity is allowed within the specified time range, otherwise `false`.
 */
export function currentGranularityAllowedInRange({
    granularityIndex,
    rangeIndex,
}: {
    granularityIndex: number;
    rangeIndex: number;
}): boolean {
    switch (rangeIndex) {
        case TIME_RANGES_INDEX.DAYS_28:
            return ([GRANULARITY_INDEX.DAILY, GRANULARITY_INDEX.WEEKLY] as number[]).includes(granularityIndex);
        case TIME_RANGES_INDEX.MONTH_3:
        case TIME_RANGES_INDEX.MONTH_6:
            return (
                [GRANULARITY_INDEX.DAILY, GRANULARITY_INDEX.WEEKLY, GRANULARITY_INDEX.MONTHLY] as number[]
            ).includes(granularityIndex);
        case TIME_RANGES_INDEX.MONTH_13:
            return ([GRANULARITY_INDEX.WEEKLY, GRANULARITY_INDEX.MONTHLY] as number[]).includes(granularityIndex);
        default:
            return false;
    }
}

/**
 * Generates an array of granularity options with their enabled/disabled state based on the current range index.
 *
 * @param {Object} params - The parameters object.
 * @param {number} params.currentRangeIndex - The index representing the current time range.
 * @returns {Array} An array of objects representing granularity options with their enabled/disabled state.
 * Each object contains:
 * - `disabled` (boolean): Indicates whether the option is disabled.
 * - `title` (string): The title of the granularity option.
 * - `value` (GranularityNames): The value representing the granularity option.
 */
export function getGranularityOptionsWithState({
    currentRangeIndex,
}: {
    currentRangeIndex: number;
}): { disabled: boolean; title: string; value: GranularityNames }[] {
    const getStateForGranularity = (granularity: GranularityNames) => {
        switch (currentRangeIndex) {
            case TIME_RANGES_INDEX.DAYS_28:
                return [GranularityNames.DAILY, GranularityNames.WEEKLY].includes(granularity);
            case TIME_RANGES_INDEX.MONTH_3:
            case TIME_RANGES_INDEX.MONTH_6:
                return [GranularityNames.DAILY, GranularityNames.WEEKLY, GranularityNames.MONTHLY].includes(
                    granularity,
                );
            case TIME_RANGES_INDEX.MONTH_13:
                return [GranularityNames.WEEKLY, GranularityNames.MONTHLY].includes(granularity);
            default:
                return false;
        }
    };

    return chartTimeGranularityOptions.map((option) => {
        const optionEnabled = getStateForGranularity(option.value);
        return {
            ...option,
            disabled: !optionEnabled,
        };
    });
}

export function IndexCell(currentPage: number = 1): ColumnsType[number]["render"] {
    return (_value, _row, currentIndex) => {
        return (currentPage - 1) * 100 + (currentIndex + 1);
    };
}
